import React from 'react';

import { Button } from '@material-ui/core';
import axios from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import FormFileInput from 'components/FormFileInput';
import FormTextInput from 'components/FormTextInput';
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from 'config/constants/upload';
import general_messages from 'messages/general_messages';
import validation_messages from 'messages/validation_messages';
import { FileInForm } from 'types/FileForm';

import DialogGrid from '../_components/DialogGrid';

export type FormDataType = {
  label: string;
  file: File | '';
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (fileData: FileInForm) => Promise<void> | void;
  withLabelField: boolean;
  acceptedTypes?: string[];
  validateFile?: (file: File) => string | null;
};

const FORM_ID = 'upload-file-form';

const getErrorMessages = (errorData: unknown): string[] => {
  if (!errorData || typeof errorData !== 'object') return [];
  const data = errorData as Record<string, unknown>;
  const fileErrors = data.file;
  if (Array.isArray(fileErrors)) return fileErrors.map(String);
  if (typeof fileErrors === 'string') return [fileErrors];
  return [];
};

const resolveUploadErrorMessage = (error: unknown, t: (value: any, options?: Record<string, unknown>) => string): string => {
  const messages = axios.isAxiosError(error) ? getErrorMessages(error.response?.data) : [];
  const combinedMessage = messages.join(' ').toLowerCase();

  if (combinedMessage.includes('large') || combinedMessage.includes('size')) {
    const maxSizeMatch = combinedMessage.match(/(\d+)\s*mb/);
    return t(validation_messages.file_too_large, { maxSizeMb: maxSizeMatch?.[1] || MAX_FILE_SIZE_MB });
  }

  if (combinedMessage.includes('type') || combinedMessage.includes('extension')) {
    return t(validation_messages.file_type_not_allowed);
  }

  return t(general_messages.api_error);
};

const UploadFileDialog: React.FC<Props> = ({ open, onClose, onSubmit, withLabelField, acceptedTypes, validateFile }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const validationSchema = React.useMemo(
    () =>
      yup.object({
        file: yup
          .mixed()
          .required()
          .test(
            'fileSize',
            `File exceeds maximum size of ${MAX_FILE_SIZE_MB} MB`,
            value => !value || (value as File).size <= MAX_FILE_SIZE_BYTES,
          ),
        label: withLabelField ? yup.string().trim().required(t(general_messages.required_field)) : yup.string(),
      }),
    [t, withLabelField],
  );

  const onFormSubmit = async (formData: FormDataType, formInstance: any) => {
    const fileInForm: FileInForm = {
      id: uuid(),
      name: formData.label,
      file: formData.file as File, // after form validation it's always a file
      isFromDb: false,
    };
    try {
      await onSubmit(fileInForm);
      formInstance.resetForm();
      onClose();
    } catch (error) {
      enqueueSnackbar(resolveUploadErrorMessage(error, t), { variant: 'error' });
    }
  };

  const formik = useFormik<FormDataType>({
    onSubmit: onFormSubmit,
    validationSchema,
    initialValues: {
      file: '',
      label: '',
    },
  });

  return (
    // @ts-ignore
    <DialogGrid
      dialogActions={
        <CenteredGrid gridGap={2} withoutPadding>
          {/* @ts-ignore */}
          <ColoredButton
            customColor='secondary'
            disabled={!formik.isValid || formik.isSubmitting}
            form={FORM_ID}
            type='submit'
            variant='outlined'
          >
            {t(general_messages.add)}
          </ColoredButton>
          <Button onClick={onClose} size='small'>
            {t(general_messages.cancel)}
          </Button>
        </CenteredGrid>
      }
      onClose={onClose}
      open={open}
      title={t(general_messages.upload_file)}
    >
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <CenteredGrid gridGap={2} withoutPadding>
          <FormFileInput
            acceptedTypes={acceptedTypes}
            formik={formik}
            id='file'
            label={t(general_messages.upload_file)}
            required
            validateFile={validateFile}
          />
          {withLabelField && <FormTextInput formik={formik} id='label' label={t(general_messages.add_label)} required />}
        </CenteredGrid>
      </form>
    </DialogGrid>
  );
};

export default UploadFileDialog;
